import { useMemo } from 'react'
import { useFormikContext } from 'formik'
import { ContentsCount } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/ContentsCount'
import { useAutopilotLimits } from 'hooks/useAutopilotLimits'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { ContentsCountProps } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/Types'
import { FormValues } from 'screens/BrandPlanSchedule/Types'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { AutopilotLockUpgradeButton } from 'components/AutopilotUpgradeButtons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Add } from 'almanac-ui/icons'
import { Tooltip } from 'components/Tooltip/Tooltip'

interface LimitedContentsCountProps extends ContentsCountProps {
  customLabel?: string
  ignoreChannelLimit?: boolean
  buttonVariant?: ContentsCountProps['buttonVariant']
  forcePlan?: PaymentPlanKey
  showLockButtons?: boolean
  lockButtonTooltip?: string
}

export const LimitedContentsCount = ({
  name,
  ignoreChannelLimit = false,
  isDisabled: isDisabledProp,
  buttonVariant,
  forcePlan,
  showLockButtons,
  lockButtonTooltip,
  ...props
}: LimitedContentsCountProps) => {
  const { values } = useFormikContext<FormValues>()
  const { maxChannels, disabledChannels, maxPostsPerChannel } = useAutopilotLimits({ forcePlan })
  const workspace = useCurrentWorkspace()

  // Get channel state information
  const channelState = useMemo(() => {
    const channelChecks = [
      { name: 'facebook_contents', contents: values.facebook_contents },
      { name: 'instagram_post_contents', contents: values.instagram_post_contents },
      { name: 'instagram_story_contents', contents: values.instagram_story_contents },
      { name: 'instagram_reel_contents', contents: values.instagram_reel_contents },
      { name: 'twitter_contents', contents: values.twitter_contents },
      { name: 'linkedin_contents', contents: values.linkedin_contents },
      { name: 'tiktok_contents', contents: values.tiktok_contents },
      { name: 'youtube_contents', contents: values.youtube_contents },
      { name: 'crosspost_contents', contents: values.crosspost_contents },
      { name: 'video_crosspost_contents', contents: values.video_crosspost_contents },
      { name: 'seo_contents', contents: values.seo_contents },
      { name: 'email_contents', contents: values.email_contents },
    ]

    const channelsWithCounts = channelChecks
      .filter(({ contents }) => contents?.some((item) => item.count > 0))
      .map(({ name }) => name)

    return {
      channelsWithCounts,
      currentChannelCount: channelsWithCounts.length,
      isChannelEmpty: !channelsWithCounts.includes(name),
    }
  }, [values, name])

  // Determine disabled reason (undefined means not disabled)
  const isDisabledReason = useMemo((): 'channel-limit' | 'freemium-disabled' | undefined => {
    // If isDisabled is explicitly provided, don't track reason
    if (isDisabledProp !== undefined) {
      return undefined
    }

    const { isChannelEmpty, currentChannelCount } = channelState

    // If the channel already has counts, it's not disabled
    if (!isChannelEmpty) {
      return undefined
    }

    // For freemium users, disable channels that are specifically disabled
    if (workspace?.is_ap_freemium && disabledChannels.includes(name)) {
      return 'freemium-disabled'
    }

    // If the channel is empty and we've reached the limit, disable it
    if (!ignoreChannelLimit && currentChannelCount >= maxChannels) {
      return 'channel-limit'
    }

    return undefined
  }, [isDisabledProp, channelState, workspace, disabledChannels, name, ignoreChannelLimit, maxChannels])

  const isDisabled = isDisabledProp ?? isDisabledReason !== undefined
  const isDisabledDueToChannelLimit = isDisabledReason === 'channel-limit'

  return (
    <Tooltip
      label={isDisabledDueToChannelLimit ? 'To enable this channel, remove another first' : ''}
      placement="top"
      hide={!isDisabledDueToChannelLimit}
    >
      <div>
        <ContentsCount
          {...props}
          name={name}
          isDisabled={isDisabled}
          maxPostsPerChannel={maxPostsPerChannel}
          buttonVariant={buttonVariant}
          lockButton={
            <Tooltip label={`Upgrade to enable more than ${maxPostsPerChannel} posts/week`} placement="top">
              <div>
                {showLockButtons ? (
                  <AutopilotLockUpgradeButton />
                ) : (
                  <IconButton size="xs" variant={buttonVariant ?? 'ghost'} icon={Add} isDisabled />
                )}
              </div>
            </Tooltip>
          }
        />
      </div>
    </Tooltip>
  )
}
